import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateDepartment,
  useUpdateDepartment,
  useDepartments,
  type DepartmentTreeNode,
  type CreateDepartmentInput,
  type UpdateDepartmentInput,
  type FlatDepartment,
 useActiveEmployees, type ActiveEmployee } from '@/hooks'
import { cn } from '@/lib/utils'

interface DepartmentFormProps {
  department?: DepartmentTreeNode
  onClose: () => void
  onSuccess?: () => void
}

export function DepartmentForm({ department, onClose, onSuccess }: DepartmentFormProps) {
  const isEdit = !!department
  const createMutation = useCreateDepartment()
  const updateMutation = useUpdateDepartment()

  const { data: departments } = useDepartments()
  const { data: employees } = useActiveEmployees()

  const [formData, setFormData] = useState({
    name: department?.name ?? '',
    code: department?.code ?? '',
    parentId: department?.parentId ?? '',
    managerId: department?.managerId ?? '',
    sortOrder: department?.sortOrder ?? 0,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {newErrors.name = '请输入部门名称'}
    if (!formData.code.trim()) {newErrors.code = '请输入部门编码'}
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {return}

    if (isEdit) {
      const data: UpdateDepartmentInput = {
        name: formData.name,
        code: formData.code,
        parentId: formData.parentId || null,
        managerId: formData.managerId || null,
        sortOrder: formData.sortOrder,
      }
      updateMutation.mutate(
        { id: department.id, data },
        { onSuccess: () => { onSuccess?.(); onClose() } }
      )
    } else {
      const data: CreateDepartmentInput = {
        name: formData.name,
        code: formData.code,
        parentId: formData.parentId || undefined,
        managerId: formData.managerId || undefined,
        sortOrder: formData.sortOrder,
      }
      createMutation.mutate(data, { onSuccess: () => { onSuccess?.(); onClose() } })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const mutationError = createMutation.error || updateMutation.error

  // 过滤掉当前部门作为父部门选项
  const parentOptions = departments?.filter((d: FlatDepartment) => d.id !== department?.id) ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{isEdit ? '编辑部门' : '新建部门'}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                部门名称 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入部门名称"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && <p className="mt-1 text-sm text-destructive">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                部门编码 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="请输入部门编码"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.code && 'border-destructive'
                )}
              />
              {errors.code && <p className="mt-1 text-sm text-destructive">{errors.code}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">上级部门</label>
              <Select
                value={formData.parentId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, parentId: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="无（顶级部门）" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">无（顶级部门）</SelectItem>
                  {parentOptions.map((dept: FlatDepartment) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">部门负责人</label>
              <Select
                value={formData.managerId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, managerId: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="请选择负责人" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">请选择负责人</SelectItem>
                  {employees?.map((emp: ActiveEmployee) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">排序</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                min={0}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {mutationError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {mutationError.message}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEdit ? '保存' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
